import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { adminPartnersService } from "@/services/admin-partners-service";
import { getPartnerRangeParams } from "@/components/pages/guesteria-partners/utils/partnerAnalyticsPeriod";
import type { PartnerRevenueDateRange } from "@/components/pages/guesteria-partners/utils/partnerRevenuePeriodPresets";
import type { PartnerDateRangeQuery } from "@/types/partner-dashboard";
import type {
  AdminPartnerOrdersSummary,
  PartnerOrdersQuery,
  PartnerOrdersResponse,
} from "@/types/partner-order";
import type { PartnerAttributionReport } from "@/types/partner-attribution";

// Task 0029 — "Глобальный summary Guesteria". Thin wrappers over
// `adminPartnersService.getOrdersSummary`/`getOrders`
// (`GET /admin/partners/orders/summary`, `GET /admin/partners/orders`),
// previously unused by any page. Query-key factory + staleTime mirror
// `partner-destinations.hooks.ts`.
export const adminPartnerRevenueKeys = {
  all: ["adminPartnerRevenue"] as const,
  summary: (query: PartnerDateRangeQuery) =>
    [...adminPartnerRevenueKeys.all, "summary", query] as const,
  orders: (query: PartnerOrdersQuery) =>
    [...adminPartnerRevenueKeys.all, "orders", query] as const,
};

const isRangeReady = (query: PartnerDateRangeQuery) =>
  Boolean(query.from) && Boolean(query.to);

export const useAdminPartnerOrdersSummary = (query: PartnerDateRangeQuery) => {
  return useQuery<AdminPartnerOrdersSummary>({
    queryKey: adminPartnerRevenueKeys.summary(query),
    enabled: isRangeReady(query),
    queryFn: () => adminPartnersService.getOrdersSummary(query),
    staleTime: 60 * 1000,
  });
};

export const useAdminPartnerOrders = (query: PartnerOrdersQuery) => {
  return useQuery<PartnerOrdersResponse>({
    queryKey: adminPartnerRevenueKeys.orders(query),
    enabled: isRangeReady(query),
    queryFn: () => adminPartnersService.getOrders(query),
    staleTime: 60 * 1000,
  });
};

// "Activities by room" report, Guesteria-admin scope (task 0041, contract
// 2026-09-30) — the hotel-card drill-down at `/analytics/hotels/:hotelId`.
// Needs the hotel's own tenantId; a mismatched one 404s (same shape as
// `getHotelActivityLimit`).
export const useAdminHotelPartnerAttribution = (
  hotelId: string | undefined,
  tenantId: string | undefined,
  range: PartnerRevenueDateRange,
  enabled = true,
) => {
  const params = useMemo(() => getPartnerRangeParams(range), [range]);

  return useQuery<PartnerAttributionReport>({
    queryKey: [
      ...adminPartnerRevenueKeys.all,
      "attribution",
      hotelId || "",
      tenantId || "",
      params.from,
      params.to,
    ] as const,
    enabled: Boolean(hotelId) && Boolean(tenantId) && enabled,
    queryFn: () =>
      adminPartnersService.getHotelAttribution(hotelId!, {
        tenantId: tenantId!,
        ...params,
      }),
    staleTime: 60 * 1000,
  });
};
